import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface StockStatus {
  totalProducts: number;
  productsInStock: number;
}

export const useStockStatus = (notificationId: string) => {
  return useQuery({
    queryKey: ['stock-status', notificationId],
    queryFn: async (): Promise<StockStatus> => {
      try {
        // Total monitored products for this notification
        const { data: monitoringRows, error: monitoringError } = await supabase
          .from('notification_product_monitoring')
          .select('id, product_sku, product_bc')
          .eq('notification_id', notificationId)
          .eq('is_monitored', true);

        if (monitoringError) {
          console.error('Error fetching monitoring rows:', monitoringError);
          throw monitoringError;
        }

        const totalProducts = (monitoringRows ?? []).length;

        if (totalProducts === 0) {
          return { totalProducts: 0, productsInStock: 0 };
        }

        // Get SKUs and barcodes to check in voorraad_live
        const skus = monitoringRows
          .map(m => m.product_sku)
          .filter((sku): sku is string => sku !== null);
        
        const barcodes = monitoringRows
          .filter(m => !m.product_sku && m.product_bc)
          .map(m => m.product_bc)
          .filter((bc): bc is string => bc !== null);

        // Fetch current stock from voorraad_live
        let stockBySku = new Map<string, number>();
        let stockByBc = new Map<string, number>();

        if (skus.length > 0) {
          const { data: voorraadData } = await supabase
            .from('voorraad_live')
            .select('product_id, voorraad')
            .in('product_id', skus);
          
          // Sum stock across all warehouses per product
          voorraadData?.forEach(item => {
            const current = stockBySku.get(item.product_id) || 0;
            stockBySku.set(item.product_id, current + (item.voorraad || 0));
          });
        }

        if (barcodes.length > 0) {
          const { data: voorraadData } = await supabase
            .from('voorraad_live')
            .select('bc, voorraad')
            .in('bc', barcodes);
          
          // Sum stock across all warehouses per barcode
          voorraadData?.forEach(item => {
            const current = stockByBc.get(item.bc) || 0;
            stockByBc.set(item.bc, current + (item.voorraad || 0));
          });
        }

        // Count products that are in stock (voorraad > 0)
        let productsInStock = 0;
        for (const row of monitoringRows) {
          let stock = 0;
          if (row.product_sku) {
            stock = stockBySku.get(row.product_sku) || 0;
          } else if (row.product_bc) {
            stock = stockByBc.get(row.product_bc) || 0;
          }
          if (stock > 0) {
            productsInStock++;
          }
        }

        return { totalProducts, productsInStock };
      } catch (err) {
        console.error('Error fetching stock status:', err);
        throw err;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - fresher data
    gcTime: 5 * 60 * 1000, // 5 minutes cache - keep in memory longer
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
